import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { teamsApi } from '../../../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const translations = {
  en: {
    title: 'Invite User',
    name: 'Name',
    email: 'Email',
    role: 'Role',
    lawyer: 'Lawyer',
    tenantAdmin: 'Tenant Admin',
    user: 'User',
    team: 'Team',
    teamOptional: 'optional',
    noTeam: 'No team',
    department: 'Department',
    deptOptional: 'optional',
    noDepartment: 'No department',
    sendEmail: 'Send invitation via email',
    cancel: 'Cancel',
    invite: 'Invite',
    inviting: 'Inviting...',
    success: 'User invited successfully!',
    tempPassword: 'Temporary password',
    error: 'Could not invite user'
  },
  no: {
    title: 'Inviter bruker',
    name: 'Navn',
    email: 'E-post',
    role: 'Rolle',
    lawyer: 'Advokat',
    tenantAdmin: 'Tenant Admin',
    user: 'Bruker',
    team: 'Team',
    teamOptional: 'valgfri',
    noTeam: 'Ingen team',
    department: 'Avdeling',
    deptOptional: 'valgfri',
    noDepartment: 'Ingen avdeling',
    sendEmail: 'Send invitasjon på e-post',
    cancel: 'Avbryt',
    invite: 'Inviter',
    inviting: 'Inviterer...',
    success: 'Bruker invitert!',
    tempPassword: 'Midlertidig passord',
    error: 'Kunne ikke invitere bruker'
  }
};

interface InviteUserModalProps {
  onClose: () => void;
  onSuccess: () => void;
  teams: any[];
  departments: any[];
  language?: string;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({ onClose, onSuccess, teams, departments, language = 'en' }) => {
  const t = translations[language] || translations.en;
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'LAWYER',
    team_id: '',
    department_id: '',
    send_email: true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        team_id: formData.team_id || null,
        department_id: formData.department_id || null
      };

      const response = await axios.post(`${API}/admin/users/invite`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t.success);
      if (response.data.temp_password) {
        toast.info(`${t.tempPassword}: ${response.data.temp_password}`, { duration: 10000 });
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to invite user:', error);
      toast.error(error.response?.data?.detail || t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">{t.title}</h2>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">{t.name} *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              placeholder="John Doe"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
            />
          </div>

          {/* Email */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">{t.email} *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              placeholder="john@example.com"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
            />
          </div>

          {/* Role */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">{t.role} *</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all bg-white"
            >
              <option value="LAWYER">{t.lawyer}</option>
              <option value="TENANT_ADMIN">{t.tenantAdmin}</option>
              <option value="USER">{t.user}</option>
            </select>
          </div>

          {/* Team */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">
              {t.team} <span className="text-gray-400 font-normal">({t.teamOptional})</span>
            </label>
            <select
              value={formData.team_id}
              onChange={(e) => setFormData({...formData, team_id: e.target.value})}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all bg-white"
            >
              <option value="">{t.noTeam}</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>

          {/* Department */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">
              {t.department} <span className="text-gray-400 font-normal">({t.deptOptional})</span>
            </label>
            <select
              value={formData.department_id}
              onChange={(e) => setFormData({...formData, department_id: e.target.value})}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all bg-white"
            >
              <option value="">{t.noDepartment}</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          {/* Send Email Checkbox */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="send_email"
              checked={formData.send_email}
              onChange={(e) => setFormData({...formData, send_email: e.target.checked})}
              className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
            />
            <label htmlFor="send_email" className="text-sm text-gray-600">
              {t.sendEmail}
            </label>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:bg-gray-300 font-medium transition-colors"
            >
              {loading ? t.inviting : t.invite}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteUserModal;
